import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import Badge from "../../components/ui-components/Badge";
import Input from "../../components/form-components/Input";
import { toast } from "../../components/ui-components/Toast";
import { createUser, listRoles } from "./api";
import { computeRoleConflicts } from "./roleConflicts";
import RoleConflictWarning from "./RoleConflictWarning";
import { isScopedRole } from "./scopeApi";

const SCOPE_ONLY_ROLE_CREATE_MESSAGE =
  "Scope-only roles require a data scope. Create the user first (without this role), then " +
  "add a data scope and assign the role from their detail page.";

// Maps the backend's SCOPE_REQUIRED (409, see backend_go
// internal/users/errors.go ErrScopeRequired) to the same two-step guidance —
// the client-side check below should normally catch this first, but the
// server is the source of truth.
function friendlyCreateError(err) {
  if (err?.serverBody?.error?.code === "SCOPE_REQUIRED") {
    return SCOPE_ONLY_ROLE_CREATE_MESSAGE;
  }
  return err?.serverBody?.error?.message || err?.message || "Failed to create user";
}

function SectionHeader({ title, helperText, statusChip }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-text">{title}</div>
        <div className="mt-0.5 text-xs text-text-2">{helperText}</div>
      </div>
      {statusChip && (
        <div className="shrink-0 flex items-center">
          <Badge status={statusChip} />
        </div>
      )}
    </div>
  );
}

const UserCreatePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [function_, setFunction] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [sendInvite, setSendInvite] = useState(true);
  const [result, setResult] = useState(null);

  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: listRoles,
    staleTime: 10 * 60 * 1000,
  });
  const roles = useMemo(() => rolesData?.roles || [], [rolesData]);
  // Only platform + organisation roles are directly assignable — templates
  // seed new organisation roles via cloning (Roles & Access catalog) and
  // must never appear as togglable chips here (backend authz
  // IsAssignableSystemRole / `is_assignable`).
  const assignableRoles = useMemo(() => roles.filter((r) => r.is_assignable), [roles]);
  const rolesById = useMemo(() => {
    const m = {};
    for (const r of roles) m[r.code] = r;
    return m;
  }, [roles]);
  const roleConflicts = useMemo(
    () => computeRoleConflicts(selectedRoles, rolesById),
    [selectedRoles, rolesById]
  );

  const mutation = useMutation({
    mutationFn: (body) => createUser(body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["managed-users"] });
      setResult(data?.result);
    },
    onError: (err) => {
      toast.error(friendlyCreateError(err));
    },
  });

  const toggleRole = (code) => {
    setSelectedRoles((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // A brand-new user has zero data-scope grants — the backend always rejects
  // a scoped role on create (SCOPE_REQUIRED). Block here rather than
  // round-tripping, and point at the two-step flow (create, then add scope +
  // assign the role from the detail page). Checked against the role objects
  // (not just codes) so an organisation role cloned from cost_owner/
  // tech_cost_owner is still caught here.
  const hasScopeOnlyRoleSelected = selectedRoles.some((code) =>
    isScopedRole(rolesById[code])
  );

  const handleSubmit = () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    if (selectedRoles.length === 0) { toast.error("Select at least one role"); return; }
    if (hasScopeOnlyRoleSelected) { toast.error(SCOPE_ONLY_ROLE_CREATE_MESSAGE); return; }
    mutation.mutate({
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      function: function_.trim() || undefined,
      roles: selectedRoles,
      send_invite: sendInvite,
    });
  };

  const resetForm = () => {
    setResult(null);
    setEmail("");
    setFirstName("");
    setLastName("");
    setFunction("");
    setSelectedRoles([]);
  };

  const isCreating = mutation.isPending;

  // No existing record on a create page — clear any stale breadcrumb tail from
  // a previously visited detail page. Called unconditionally (hooks rule).
  useSetHeaderRecordName(null);

  if (result) {
    const inviteToken = result.invite_url;
    const baseUrl = window.location.origin;
    const inviteLink = inviteToken ? `${baseUrl}/invite?token=${inviteToken}` : null;

    return (
      <div className="h-full flex flex-col bg-background-grey relative">
        {/* Title lives in the header's row-1 title slot. */}
        <HeaderPortal slot="title">
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
              Invite User
            </div>
          </div>
        </HeaderPortal>

        {/* Action buttons live in the header's row-1 actions slot. */}
        <HeaderPortal>
          <div className="flex items-center gap-2.5">
            <button type="button" className={secondaryButtonCls} onClick={() => navigate("/settings/users")}>
              Back to users
            </button>
            <button type="button" className={primaryButtonCls} onClick={resetForm}>
              Invite another
            </button>
          </div>
        </HeaderPortal>

        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-[680px]">
            <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
              <div className="border-b border-border pb-3 mb-4">
                <h3 className="text-sm font-semibold text-text">User Created</h3>
                <p className="text-xs text-text-2 mt-0.5">The user has been invited successfully.</p>
              </div>

              <div className="space-y-4">
                <section className="rounded-lg border border-success/30 bg-success-soft p-4">
                  <SectionHeader title="Success" helperText={`${result.user?.email} has been invited.`} />
                  {inviteLink && (
                    <div className="mt-3">
                      <label className="block mb-1 text-sm font-regular text-text-2">Invite link</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={inviteLink}
                          className="flex-1 form-control border border-border w-full h-11 p-2 rounded-md bg-surface-2 text-text-2 text-sm"
                        />
                        <button
                          onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Link copied to clipboard"); }}
                          className="px-3 py-1.5 rounded text-sm border border-border bg-primary text-white border-transparent hover:bg-primary-hover transition-colors cursor-pointer"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      {/* Title lives in the header's row-1 title slot. */}
      <HeaderPortal slot="title">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text truncate font-['Plus_Jakarta_Sans',sans-serif]">
            Invite User
          </div>
        </div>
      </HeaderPortal>

      {/* Action buttons live in the header's row-1 actions slot. */}
      <HeaderPortal>
        <div className="flex items-center gap-2.5">
          <button type="button" className={secondaryButtonCls} onClick={() => navigate("/settings/users")}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryButtonCls}
            onClick={handleSubmit}
            disabled={isCreating}
          >
            {isCreating ? "Creating..." : "Create & Invite"}
          </button>
        </div>
      </HeaderPortal>

      {/* Scrollable content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[680px]">
          {/* Profile card */}
          <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
            <div className="border-b border-border pb-3 mb-4">
              <h3 className="text-sm font-semibold text-text">Profile</h3>
              <p className="text-xs text-text-2 mt-0.5">Set up the new user's information.</p>
            </div>

            <div className="space-y-4">
              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader title="Email" helperText="Required. The user will receive their invite here." />
                <div className="mt-3">
                  <Input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.com"
                    noMarginBottom
                  />
                </div>
              </section>

              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader title="Name" helperText="Optional. Can be updated later." />
                <div className="mt-3 flex gap-4">
                  <div className="flex-1">
                    <Input
                      title="First name"
                      name="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      noMarginBottom
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      title="Last name"
                      name="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      noMarginBottom
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader title="Function" helperText="Job title or role in the organisation. Optional." />
                <div className="mt-3">
                  <Input
                    title="Function"
                    name="function"
                    type="text"
                    value={function_}
                    onChange={(e) => setFunction(e.target.value)}
                    placeholder="e.g. Finance Manager"
                    noMarginBottom
                  />
                </div>
              </section>

              <div className="border-t border-border" aria-hidden />

              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader
                  title="Roles"
                  helperText="Determines what the user can access."
                  statusChip={selectedRoles.length > 0 ? `${selectedRoles.length} assigned` : "no roles"}
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {assignableRoles.map((role) => (
                    <button
                      key={role.code}
                      type="button"
                      onClick={() => toggleRole(role.code)}
                      className={`px-3 py-1.5 rounded text-sm border border-border transition-colors cursor-pointer ${
                        selectedRoles.includes(role.code)
                          ? "bg-primary text-white border-transparent"
                          : "bg-card text-text-3 border-border hover:border-border-strong hover:bg-surface-2"
                      }`}
                    >
                      {role.name}
                    </button>
                  ))}
                </div>
                <RoleConflictWarning conflicts={roleConflicts} />
                {hasScopeOnlyRoleSelected && (
                  <p className="mt-2 text-xs text-danger">{SCOPE_ONLY_ROLE_CREATE_MESSAGE}</p>
                )}
              </section>

              <div className="border-t border-border" aria-hidden />

              <section className="rounded-lg border border-border bg-surface-2/50 p-4">
                <SectionHeader title="Invitation" helperText="Control how the invite is delivered." />
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendInvite"
                    checked={sendInvite}
                    onChange={(e) => setSendInvite(e.target.checked)}
                    className="rounded border-border accent-primary"
                  />
                  <label htmlFor="sendInvite" className="text-sm text-text-2">Send invite email</label>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCreatePage;
